import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import useLocalization from '../../../hooks/useLocalization';
import { useCryptoDeposit, CRYPTO_DEPOSIT_STATUS } from '../../../hooks/payment/useCryptoDeposit';
import CryptoDepositSkeleton from './CryptoDepositSkeleton';

const styles = {
  root: {
    gap: 1.5,
    minHeight: { xs: 220, md: 240 },
  },
  depositContainer: {
    backgroundColor: 'neutral.600',
    borderRadius: 3,
    p: { xs: 3, md: 3.5 },
  },
  depositContent: {
    alignItems: 'center',
    gap: 1.5,
  },
  qrContainer: {
    display: 'flex',
    justifyContent: 'center',
    p: 1,
    backgroundColor: 'neutral.700',
    borderRadius: 2,
    width: 'fit-content',
  },
  qrImage: {
    width: { xs: 120, md: 140 },
    height: 'auto',
    display: 'block',
  },
  addressSection: {
    gap: 0.75,
    width: 1,
  },
  addressLabel: {
    typography: 'caption',
    color: 'text.secondary',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    pl: 1,
  },
  addressField: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minHeight: 44,
    px: 1.5,
    py: 0.75,
    borderRadius: 2,
    backgroundColor: 'neutral.700',
  },
  addressValue: {
    flex: 1,
    color: 'neutral.25',
    typography: 'caption',
    fontWeight: 700,
    wordBreak: 'break-all',
  },
  copyButton: {
    color: 'neutral.300',
    '&:hover': {
      color: 'primary.main',
    },
  },
  copiedButton: {
    color: 'primary.main',
  },
  retryButton: {
    alignSelf: 'center',
  },
};

type CryptoDepositFormProps = {
  paymentProvider: string | null;
};

const CryptoDepositContent = ({ paymentProvider }: CryptoDepositFormProps) => {
  const { t } = useLocalization();
  const { status, address, qrCodeSrc, copied, triggerDeposit, handleCopyAddress } = useCryptoDeposit(paymentProvider);

  const showDepositSkeleton = status === CRYPTO_DEPOSIT_STATUS.LOADING;
  const showDepositContent = status === CRYPTO_DEPOSIT_STATUS.READY;

  return (
    <Stack sx={styles.root}>
      {showDepositSkeleton && <CryptoDepositSkeleton />}
      {status === CRYPTO_DEPOSIT_STATUS.ERROR && (
        <Button variant='contained' onClick={triggerDeposit} sx={styles.retryButton}>
          {t('retry')}
        </Button>
      )}
      {showDepositContent && (
        <Box sx={styles.depositContainer}>
          <Stack sx={styles.depositContent}>
            {qrCodeSrc && (
              <Box sx={styles.qrContainer}>
                <Box component='img' src={qrCodeSrc} alt={t('cryptoQrCodeAlt')} sx={styles.qrImage} />
              </Box>
            )}
            <Stack sx={styles.addressSection}>
              <Typography sx={styles.addressLabel}>{t('cryptoAddress')}</Typography>
              <Box sx={styles.addressField}>
                <Typography sx={styles.addressValue}>{address}</Typography>
                <IconButton
                  onClick={handleCopyAddress}
                  size='small'
                  sx={copied ? styles.copiedButton : styles.copyButton}
                  aria-label={t('copyWalletAddress')}
                >
                  {copied ? <CheckIcon fontSize='small' /> : <ContentCopyIcon fontSize='small' />}
                </IconButton>
              </Box>
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const CryptoDepositForm = (props: CryptoDepositFormProps) => (
  <CryptoDepositContent key={props.paymentProvider} {...props} />
);

export default CryptoDepositForm;
